import * as Controls from "./controls";
import * as Rules from "./rules";
import * as Session from "./controls/extended-session";
import * as Timer from "./controls/timer";

import {
    Component,
    FormComponentProps,
    FormComponentSettings,
    FormContext,
    FormControl,
    FormValidationRule,
    FormValidationRuleWithState,
    FormValues,
    PaginatedElement,
    iPageTemplate,
    GravityResponse
} from "./types";
import { PcaHeading, PcaSubHeading } from "../theme/typography";
import React, { FunctionComponent, useEffect, useReducer } from "react";
import createFormContext, {
    getControlProps,
    pageMax,
    paginateComponents,
    validator
} from "./form-context";
import {
    createStateReducer,
    gotoReducer,
    validationReducer,
    valuesReducer
} from "./form-reducer";

import LoadingIcon from "../loading-icon/loading-icon";
import createSubmission from "./submission";
import { replaceMergeTagsInString } from "./helper";

export const Context = React.createContext({});

const UndefinedControl = ({ type }: { type: string }) => (
    <>{`Undefined type ${type}`}</>
);

/**
 * Default control components
 */
const defaultControls = (): { [key: string]: Component } => ({
    actions: Controls.Buttons,
    bif_auth_field: Controls.BIFAuth,
    checkbox: Controls.Checkbox,
    date: Controls.DateInput,
    date_v2: Controls.DateV2,
    default: Controls.ToJson,
    email: Controls.Email,
    fileupload: Controls.FileUpload,
    hidden: Controls.Hidden,
    html: Controls.Html,
    multiselect: Controls.MultiSelect,
    onfido_sdk: Controls.OnFidoSDK,
    onfido_sdk_check: Controls.OnFidoSDKCheck,
    page: Controls.Page,
    phone: Controls.Telephone,
    radio: Controls.Radio,
    select: Controls.Select,
    conditionalSelect: Controls.Select,
    submit: Controls.Buttons,
    text: Controls.TextInput,
    number: Controls.NumberInput,
    textarea: Controls.TextArea,
    boi_textarea: Controls.TextArea,
    iban: Controls.Iban,
    account_number: Controls.AccountNumber,
    boi_giin: Controls.Giin,
    boi_unsupported_browser: Controls.UnsupportedBrowser,
    boi_camera_detection: Controls.CameraDetection,
    boi_uuid: Controls.UUID,
    timer: Controls.TimerField,
    user_session: Session.SessionField,
    formactions: Controls.FormActions,
    "table-drop-down": Controls.TableDropdown,
    "table-searcher": Controls.TableSearcher,
    time: Controls.Time,
    "sort-code": Controls.SortCode,
    "sort_code_v2": Controls.SortCodeV2,
    boi_slider: Controls.Slider,
    name: Controls.NameField,
    boi_phone: Controls.BoiPhone,
    tos: Controls.TermsOfService,
    boi_date_range: Controls.DateRange,
    boi_loan_calculator: Controls.LoanCalculator,
    conditionalRadio: Controls.Radio,
    conditionalCheckbox: Controls.Checkbox,
    pdfLinkTracker: Controls.PDFLinkTracker,
    searchList: Controls.SearchList,
    form_module: Controls.Hidden,
});

const defaultRules: (
    | FormValidationRule
    | FormValidationRuleWithState
)[] = Object.values(Rules);

/**
 * Component
 */

type FormControlOnChange = {
    (value: any, form: FormContext): void;
};

const createInitialState = (
    formControls: FormControl[],
    rules: (FormValidationRule | FormValidationRuleWithState)[],
    values: FormValues,
    page: number,
    children: PaginatedElement[],
    settings: FormComponentSettings,
    response?: GravityResponse
) => {
    const additional: PaginatedElement[] = renderControls({
        controls: formControls,
        settings
    });
    const controls =
    settings.controlPlacement === "afterChildren"
        ? children.concat(additional)
        : additional.concat(children);
    const controlProps = getControlProps(controls);
    const initialState = {
        controls,
        controlProps,
        validation: {},
        values,
        page,
        pagination: paginateComponents(controls),
        rules,
        visitedLastPage: false,
        response
    };
    initialState.validation = validator(initialState);
    if (response && !response.isValid) {
        initialState.page = pageMax(initialState, true);
    }

    return initialState;
};

const createOnSubmitHandler = (
    context: FormContext,
    setTempValues?: (values: { [key: string]: any }) => void,
    onSubmit?: (data: FormData, context: FormContext) => void
) => () => {
    /**
     * TODO: Allow additional preSubmission adaptors
     */
    const submission = [
        Timer.preSubmissionAdaptor,
        Session.preSubmissionAdaptor
    ].reduce((f, c) => c(f, context), context.getValues());
    // Save values from the context as temp values
    setTempValues && setTempValues(submission);
    onSubmit && onSubmit(createSubmission(submission), context);
};

/** Generate the reducer outside of the component otherwise actions maybe dispatched multiple times */
const formReducer = createStateReducer([
    valuesReducer,
    validationReducer,
    gotoReducer
]);

const FormComponent = ({
    button,
    title = "",
    description = "",
    controls: formControls = [],
    rules = [],
    defaultValues = {},
    settings = {},
    initialPage = 1,
    onChange,
    onPage,
    onError, // TODO
    onSubmit,
    children,
    inAccordion = false,
    tempValues = {},
    setTempValues,
    response
}: FormComponentProps) => {
    /**
     * State
     */
    const [state, dispatchAction] = useReducer(formReducer, {}, () => (
        createInitialState(
            formControls,
            [...defaultRules, ...rules],
            Object.keys(tempValues).length ? tempValues : defaultValues,
            initialPage,
            React.Children.toArray(children) as PaginatedElement[],
            settings,
            response
        )
    ));
    /**
     * Effects
     */
    const context = createFormContext(
        {
            button,
            title,
            controls: formControls,
            description
        },
        {
            ...state,
            dispatchAction
        }
    );
    useEffect(initEffect(context, tempValues, setTempValues), []);
    useEffect(valuesEffect(context, onChange), [state.values]);
    useEffect(pageEffect(context, onPage), [state.page]);
    useEffect(scrollEffect(inAccordion), [state.page]);
    return (
        <Context.Provider value={context}>
            <FormRenderer
                context={context}
                template={settings.templateComponent || DefaultTemplate}
                onSubmit={createOnSubmitHandler(context, setTempValues, onSubmit)}
                inAccordion={inAccordion}
            />
        </Context.Provider>
    );
};

const renderControls = ({
    controls,
    settings
}: {
    controls: FormControl[];
    settings: FormComponentSettings;
}) => {
    const components = {
        ...defaultControls(),
        ...(settings.pluginControls || {})
    };
    return controls.map((control, key) =>
        renderControl(components, { key: `${key}`, ...control })
    );
};

const renderControl = (
    components: { [key: string]: Component },
    control: FormControl
) => {
    const component = components[control.type] || UndefinedControl;
    return React.createElement(component, control) as PaginatedElement;
};

const initEffect = (
    context: FormContext,
    tempValues: FormValues,
    setTempValues?: (values: { [key: string]: any }) => void
) => () => {
    // Emptying temp values
    Object.keys(tempValues).length && setTempValues && setTempValues({});
};

const valuesEffect = (
    context: FormContext,
    callback?: (values: FormValues, context: FormContext) => void
) => () => {
    callback && callback(context.getValues(), context);
};

const pageEffect = (
    context: FormContext,
    callback?: (page: number, context: FormContext) => void
) => () => {
    callback && callback(context.state.page, context);
};

const scrollEffect = (inAccordion: boolean) => () => {
    !inAccordion && document.body.scrollIntoView();
};

const FormRenderer: FunctionComponent<{
    context: FormContext;
    template: iPageTemplate;
    onSubmit?: () => void;
    inAccordion?: boolean;
}> = ({ context, template, onSubmit, inAccordion }) => {
    const { children: unControlled, ...page } = context.getPage();
    const children = React.Children.map(unControlled, child =>
        React.isValidElement(child) ? renderChild(child, context) : child
    );
    return template({
        ...page,
        context,
        onSubmit,
        lastTabIndex: children.length,
        children,
        inAccordion
    });
};

const DefaultTemplate: iPageTemplate = ({
    context,
    onSubmit,
    children,
    lastTabIndex,
    ...props
}) => {
    const childarray = React.Children.toArray(children);
    return (
        <form
            onSubmit={e => {
                e.preventDefault();
                return false;
            }}
        >
            {context.form.title && (
                <PcaHeading>{context.form.title}</PcaHeading>
            )}
            {context.form.description && (
                <PcaSubHeading>{context.form.description}</PcaSubHeading>
            )}
            {childarray.length ? (
                <>
                    <div className={`controls ${props.cssClass}`}>
                        {childarray.map(c =>
                            !React.isValidElement(c) || !c.props.hidden
                                ? c
                                : null
                        )}
                    </div>
                    <Controls.FormActions
                        {...props}
                        context={context}
                        onSubmit={onSubmit}
                    />
                </>
            ) : (
                <LoadingIcon />
            )}
        </form>
    );
};

const renderChild = (child: JSX.Element, context: FormContext) => {
    if (child.props.name) {
        return React.cloneElement(child, formControlProps(child, context));
    }
    return child;
};

const formControlProps = (node: React.ReactElement, context: FormContext) => {
    const name = node.props.name;
    const label = node.props.label
        ? replaceMergeTagsInString(node.props.label, context.getValues())
        : "";
    const description = node.props.description
        ? replaceMergeTagsInString(node.props.description, context.getValues())
        : "";
    const placeholder = node.props.placeholder
        ? replaceMergeTagsInString(node.props.placeholder, context.getValues())
        : "";
    const value = context.getValue(name, "");
    const isDirty = context.isDirty(name);
    const error =
        isDirty && context.state.validation[name]
            ? context.state.validation[name][0]
            : context.state.response
                && context.state.response.validationMessages
                && name in context.state.response.validationMessages
                && !context.state.response.validationMessages[name].isEdited
            ? context.state.response.validationMessages[name].error
            : "";
    const onChange = formControlOnChange(name, context, node.props.onChange);
    return {
        value,
        label,
        description,
        placeholder,
        error,
        key: `form[${name}]`,
        onChange,
        context,
        hidden: context.isHidden(name)
    };
};

const formControlOnChange = (
    name: string,
    context: FormContext,
    onChange?: FormControlOnChange
) => (value: any) => {
    name !== undefined && context.setValue(name, value);
    if (context.state.response && context.state.response.validationMessages
        && name in context.state.response.validationMessages
        && !context.state.response.validationMessages[name].isEdited) {
            context.state.response.validationMessages[name].isEdited = true;
    }
    onChange && onChange(value, context);
};

export default FormComponent;
export { UndefinedControl };
